import React, { useCallback, useEffect, useState } from "react";
import {
  listEvidence,
  submitEvidence,
  voteEvidence,
  flagEvidence,
} from "@/api";
import { EVIDENCE } from "@/constants/testIds";

const formatTime = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
};

// Map evidence.status -> { badge label, pill variant }.
// Used both inside the COMMUNITY-ELEVATED block (promoted only)
// and the EVIDENCE UNDER REVIEW block (rising / pending / flagged).
const STATUS_BADGE = {
  promoted: { label: "PROMOTED BY CELL VOTE", pillClass: "active" },
  rising: { label: "RISING", pillClass: "active" },
  flagged: { label: "UNDER REVIEW", pillClass: "warn" },
  pending: { label: "PENDING", pillClass: "" },
};

// ------- Single card -------
const EvidenceCardRow = ({ card, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const cast = async (dir) => {
    setBusy(true);
    setErr(null);
    try {
      await voteEvidence(card.hash, dir);
      await onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not vote.";
      setErr(detail);
    } finally {
      setBusy(false);
    }
  };

  const flag = async (reason) => {
    setBusy(true);
    setErr(null);
    setFlagOpen(false);
    try {
      await flagEvidence(card.hash, reason);
      await onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not flag.");
    } finally {
      setBusy(false);
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/evidence/${card.hash}`;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        // both clipboard APIs failed (browser blocked / sandboxed iframe);
        // visual feedback still fires below so the user knows the click registered.
        console.warn("clipboard copy fallback failed");
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const status = card.status || "pending";
  const badge = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const cardVariant = status; // .evidence-card.promoted | .rising | .flagged | .pending
  const shortHash = `0x${card.hash.slice(0, 8)}`;

  return (
    <div
      data-testid={EVIDENCE.card(card.hash)}
      className={`evidence-card ${cardVariant}`}
    >
      <div className="evidence-card-header">
        <span
          data-testid={EVIDENCE.cardBadge(card.hash)}
          className={`pill ${badge.pillClass}`}
          title={`Community votes (up / down): +${card.upvotes} / ${card.downvotes}`}
        >
          {badge.label}
        </span>
        <span className="evidence-card-class">
          {(card.type || "").toUpperCase()}
        </span>
        <span className="muted" style={{ fontSize: 11 }}>
          {card.submitted_by} · {formatTime(card.submitted_at)}
        </span>
        <span className="faint" style={{ fontSize: 11, marginLeft: "auto" }}>
          {shortHash}
        </span>
      </div>

      <div className="evidence-card-body">{card.content}</div>

      {card.source_url && (
        <a
          href={card.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="evidence-source"
        >
          {"→ "}source
        </a>
      )}

      <div className="evidence-card-footer">
        <button
          data-testid={EVIDENCE.voteUp(card.hash)}
          className={`vote-btn ${card.user_vote === "up" ? "active up" : ""}`}
          onClick={() => cast("up")}
          disabled={busy || card.user_vote === "up"}
          aria-label="upvote evidence"
        >
          ▲
        </button>
        <span
          className="muted"
          style={{ fontSize: 12, minWidth: 24, textAlign: "center" }}
          title="Community votes (up / down)"
        >
          {card.vote_score >= 0 ? "+" : ""}
          {card.vote_score}
        </span>
        <button
          data-testid={EVIDENCE.voteDown(card.hash)}
          className={`vote-btn ${card.user_vote === "down" ? "active down" : ""}`}
          onClick={() => cast("down")}
          disabled={busy || card.user_vote === "down"}
          aria-label="downvote evidence"
        >
          ▼
        </button>
        <span
          className="faint"
          style={{ fontSize: 11, marginLeft: "0.5rem" }}
          title="Community votes (up / down)"
        >
          (+{card.upvotes} / {card.downvotes})
        </span>
        <button
          data-testid={EVIDENCE.shareBtn(card.hash)}
          className={`ghost share-btn ${copied ? "copied" : ""}`}
          onClick={copyShareLink}
          style={{
            marginLeft: "auto",
            padding: "0.18rem 0.55rem",
            fontSize: 11,
            minWidth: 92,
          }}
          aria-label="copy share link"
        >
          {copied ? "LINK COPIED" : "↗ SHARE"}
        </button>
        <div style={{ position: "relative" }}>
          <button
            data-testid={EVIDENCE.flagBtn(card.hash)}
            className="ghost"
            style={{ padding: "0.15rem 0.55rem", fontSize: 11 }}
            onClick={() => setFlagOpen((v) => !v)}
            disabled={busy}
          >
            ⋯
          </button>
          {flagOpen && (
            <div
              data-testid={EVIDENCE.flagMenu(card.hash)}
              className="flag-menu"
            >
              <div className="kbd-label" style={{ fontSize: 10, marginBottom: 4 }}>
                FLAG AS…
              </div>
              {["spam", "harmful", "duplicate", "offtopic"].map((r) => (
                <button
                  key={r}
                  className="ghost"
                  onClick={() => flag(r)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    fontSize: 11,
                    padding: "0.3rem 0.5rem",
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {err && (
        <div style={{ color: "var(--ax-danger)", fontSize: 11, marginTop: 6 }}>
          {err}
        </div>
      )}
    </div>
  );
};

// ------- Submission form -------
const SubmitForm = ({ slug, onSubmitted, onCancel, canSubmit, denyReason }) => {
  const [type, setType] = useState("supporting");
  const [content, setContent] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    if (content.trim().length < 8) {
      setErr("Evidence must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const data = await submitEvidence(slug, {
        type,
        content: content.trim(),
        source_url: sourceUrl.trim() || undefined,
      });
      setReceipt(data.hash);
      setContent("");
      setSourceUrl("");
      await onSubmitted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(
        typeof detail === "string"
          ? detail
          : "Could not submit evidence. Check the format and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!canSubmit) {
    return (
      <div className="term-card" data-testid={EVIDENCE.submitForm}>
        <div className="kbd-label" style={{ color: "var(--ax-warn)" }}>
          // submission gated
        </div>
        <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
          {denyReason || "You can't submit evidence right now."}
        </div>
        <div style={{ marginTop: "0.8rem" }}>
          <button className="ghost" onClick={onCancel}>
            CLOSE
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="term-card"
      data-testid={EVIDENCE.submitForm}
      onSubmit={handle}
    >
      <div className="kbd-label">$ submit evidence --type=...</div>

      <div style={{ display: "flex", gap: "0.5rem", margin: "0.6rem 0" }}>
        <button
          type="button"
          data-testid={EVIDENCE.submitType}
          className={type === "supporting" ? "" : "ghost"}
          onClick={() => setType("supporting")}
          style={{ flex: 1 }}
        >
          SUPPORTING
        </button>
        <button
          type="button"
          className={type === "counter" ? "" : "ghost"}
          onClick={() => setType("counter")}
          style={{ flex: 1 }}
        >
          COUNTER
        </button>
      </div>

      <textarea
        data-testid={EVIDENCE.submitContent}
        value={content}
        onChange={(e) => setContent(e.target.value.slice(0, 1000))}
        placeholder="Paste a specific, verifiable claim with a year/source if possible..."
        rows={4}
        maxLength={1000}
      />
      <div className="faint" style={{ fontSize: 11, marginTop: 4, textAlign: "right" }}>
        {content.length}/1000
      </div>

      <label className="kbd-label" htmlFor="sourceUrl" style={{ marginTop: 8, display: "block" }}>
        SOURCE URL (must be https://) — optional
      </label>
      <input
        id="sourceUrl"
        data-testid={EVIDENCE.submitSourceUrl}
        value={sourceUrl}
        onChange={(e) => setSourceUrl(e.target.value)}
        placeholder="https://..."
        type="url"
      />

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.8rem" }}>
        <button
          type="submit"
          data-testid={EVIDENCE.submitButton}
          disabled={busy || content.trim().length < 8}
        >
          {busy ? "SUBMITTING…" : "SUBMIT EVIDENCE"}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          CANCEL
        </button>
      </div>

      {err && (
        <div style={{ color: "var(--ax-danger)", fontSize: 12, marginTop: 8 }}>
          {err}
        </div>
      )}
      {receipt && (
        <div
          data-testid={EVIDENCE.submitReceipt}
          className="evidence-receipt"
          style={{ marginTop: 10 }}
        >
          <div className="kbd-label" style={{ color: "var(--ax-accent)" }}>
            RECEIPT
          </div>
          <div style={{ marginTop: 4 }}>
            Submission accepted. Your hash:{" "}
            <span className="glow" style={{ fontWeight: 700 }}>
              0x{receipt.slice(0, 8)}…{receipt.slice(-6)}
            </span>
          </div>
          <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>
            Pending community review. Promoted once it clears the credibility threshold.
          </div>
        </div>
      )}
    </form>
  );
};

// ------- Main section -------
// V1.1 layout: two sections, separated by status.
//   - COMMUNITY-ELEVATED EVIDENCE   ← status="promoted"
//   - EVIDENCE UNDER REVIEW          ← status in {"pending", "rising", "flagged"}
// Each card shows its own SUPPORTING / COUNTER classification label and a
// status-aware badge. Hidden ("hidden", "rejected") evidence never reaches us.
const EvidenceSection = ({ slug, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await listEvidence(slug);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const canSubmit = user
    ? user.subscription_status === "active" ||
      (user.lifetime_submissions || 0) < (user.free_submission_limit || 3)
    : false;

  const denyReason = !user
    ? "Sign in (refresh the page) to submit."
    : !canSubmit
    ? `Free tier allows ${user.free_submission_limit || 3} lifetime submissions. Upgrade for unlimited evidence submissions and dossier creation.`
    : null;

  if (loading)
    return (
      <p className="muted">
        $ loading community evidence<span className="cursor" />
      </p>
    );
  if (!data) return null;

  // Promoted = any classification, in a single elevated list.
  const promoted = [
    ...(data.promoted_supporting || []),
    ...(data.promoted_counter || []),
  ];

  // Under review = pending + rising + flagged (already pre-filtered by API).
  // Sort: rising first (most momentum), then plain pending, then flagged.
  const STATUS_ORDER = { rising: 0, pending: 1, flagged: 2 };
  const underReview = [
    ...(data.pending_supporting || []),
    ...(data.pending_counter || []),
  ].sort((a, b) => {
    const oa = STATUS_ORDER[a.status] ?? 9;
    const ob = STATUS_ORDER[b.status] ?? 9;
    if (oa !== ob) return oa - ob;
    return (b.vote_score || 0) - (a.vote_score || 0);
  });

  return (
    <section
      id="evidence"
      data-testid={EVIDENCE.section}
      className="evidence-section"
      style={{ margin: "1.5rem 0" }}
    >
      <div className="kbd-label">$ ls /community-evidence/</div>

      {/* ---- COMMUNITY-ELEVATED EVIDENCE ---- */}
      <div
        data-testid={EVIDENCE.elevatedSection}
        className="evidence-section-block elevated"
      >
        <h3>COMMUNITY-ELEVATED EVIDENCE</h3>
        <p className="evidence-section-subcopy">
          Promoted evidence is sourced by AXIOM users and elevated by cell votes.
          These entries have cleared the vote and flag thresholds for this dossier.
        </p>
        <div data-testid={EVIDENCE.elevatedList}>
          {promoted.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              $ no community-elevated evidence yet. Vote on submissions below to promote them.
            </p>
          ) : (
            promoted.map((c) => (
              <EvidenceCardRow key={c.hash} card={c} onChange={load} />
            ))
          )}
        </div>
      </div>

      {/* ---- EVIDENCE UNDER REVIEW ---- */}
      <div
        data-testid={EVIDENCE.underReviewSection}
        className="evidence-section-block under-review"
      >
        <h3>EVIDENCE UNDER REVIEW</h3>
        <p className="evidence-section-subcopy">
          Pending submissions appear here until the cell decides if they belong in the dossier.
        </p>
        <div data-testid={EVIDENCE.underReviewList}>
          {underReview.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              $ no submissions pending review. Be the first.
            </p>
          ) : (
            underReview.map((c) => (
              <EvidenceCardRow key={c.hash} card={c} onChange={load} />
            ))
          )}
        </div>

        <div style={{ marginTop: "1rem" }}>
          {!showForm ? (
            <button
              data-testid={EVIDENCE.submitOpen}
              onClick={() => setShowForm(true)}
            >
              + SUBMIT EVIDENCE
            </button>
          ) : (
            <SubmitForm
              slug={slug}
              onSubmitted={load}
              onCancel={() => setShowForm(false)}
              canSubmit={canSubmit}
              denyReason={denyReason}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default EvidenceSection;
